// Reminders / automated WhatsApp messages. Same layout as the reference "Automated Messages" page
// (header, tab bar, automation cards with a step table, message history, settings, templates).
// Automation Settings is where staff turn messages on or off and change their timing.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api, { formatDateTime, formatPhone, SALON_NAME } from '../services/api';
import { EmptyState, Loader, Modal, Pagination, Spinner, usePagination } from '../components/ui';
import AutomationSettings from './AutomationSettings';

const TABS = [
  { id: 'automations', label: 'Automations', icon: '🤖' },
  { id: 'history', label: 'Message History', icon: '📋' },
  { id: 'settings', label: 'Automation Settings', icon: '⚙️' },
  { id: 'templates', label: 'Message Templates', icon: '📝' },
];

const FLOW_ICON = { booking: '📅', idle: '💬', after_service: '💆', missed: '🕒', other: '•' };
// Used until the backend's /templates list arrives (or when an older backend has none).
const FLOW_NAMES = {
  booking: 'Booking Reminders',
  idle: 'Unfinished Booking Nudge',
  after_service: 'Return Visit Reminders',
  missed: 'Missed Appointment',
};

const STATUS_COLOR = {
  active: '#2e7d32',
  cancelled: '#c62828',
  completed: '#1d7c77',
  pending: '#b7791f',
  sent: '#2e7d32',
  delivered: '#3b6fd1',
  read: '#0f8f6a',
  failed: '#c62828',
};
const STATUS_LABEL = {
  active: 'Running',
  cancelled: 'Stopped',
  completed: 'Done',
  pending: 'Waiting to send',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
};
const DONE_STATUSES = ['sent', 'delivered', 'read'];

const META_CHECK = {
  approved: { label: 'Approved on WhatsApp', tone: 'ok' },
  missing: { label: 'Not on the WhatsApp account', tone: 'danger' },
  body_differs: { label: 'Approved text differs', tone: 'warn' },
  pending: { label: 'Waiting for Meta approval', tone: 'warn' },
  rejected: { label: 'Rejected by Meta', tone: 'danger' },
  unknown: { label: 'Not checked', tone: 'muted' },
};

function SentAs({ value }) {
  if (!value) return <span className="nye-muted">—</span>;
  const text = value === 'text';
  return (
    <span
      className={`nye-pill nye-pill--sm nye-pill--square${text ? ' nye-pill--info' : ''}`}
      title={text ? 'The customer wrote in the last 24 hours, so plain text was sent' : 'Approved WhatsApp template'}
    >
      {text ? 'Text (24h window)' : 'Template'}
    </span>
  );
}

function Badge({ status, small }) {
  const c = STATUS_COLOR[status] || '#777';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: small ? '2px 8px' : '3px 10px',
        borderRadius: 999,
        fontSize: small ? 10 : 11,
        fontWeight: 700,
        background: `${c}18`,
        color: c,
        border: `1px solid ${c}33`,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: c }} />
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function groupStatus(steps) {
  if (steps.some((s) => s.status === 'pending')) return 'active';
  if (steps.some((s) => s.status === 'cancelled')) return 'cancelled';
  return 'completed';
}

const BOOKING_KINDS = ['confirmation', 'reminder_24h', 'reminder_1h', 'stylist_assigned', 'rescheduled'];

/** Flow of a job; older backends do not send `flow`, so derive it from the kind. */
function flowOf(job) {
  if (job.flow) return job.flow;
  if (BOOKING_KINDS.includes(job.kind)) return 'booking';
  if (String(job.kind || '').startsWith('idle_nudge')) return 'idle';
  if (String(job.kind || '').startsWith('missed_')) return 'missed';
  return 'after_service';
}

/** Compare two ISO date strings that may be missing. */
function byDate(a, b) {
  return String(a || '').localeCompare(String(b || ''));
}

function buildGroups(jobs) {
  const map = new Map();
  (Array.isArray(jobs) ? jobs : []).forEach((j) => {
    const flow = flowOf(j);
    const key = flow === 'idle' ? `${j.customer_id}:idle` : `${j.customer_id}:${flow}:${j.appointment_id || ''}`;
    if (!map.has(key)) {
      map.set(key, { key, flow, customer_id: j.customer_id, customer_name: j.customer_name, customer_phone: j.customer_phone, appointment_id: j.appointment_id, steps: [] });
    }
    map.get(key).steps.push(j);
  });
  return [...map.values()]
    .map((g) => {
      const steps = [...g.steps].sort((a, b) => byDate(a.send_at, b.send_at) || a.id - b.id);
      // Started = earliest created time; fall back to the earliest send time when created_at is absent.
      const started = steps.map((s) => s.created_at || s.send_at || '').filter(Boolean).sort()[0] || '';
      return { ...g, steps, status: groupStatus(steps), started };
    })
    .sort((a, b) => byDate(b.started, a.started));
}

const VAR_PATTERN = /(\{\{\d+\}\})/g;

function highlight(body) {
  return String(body)
    .split(VAR_PATTERN)
    .map((part, i) => (/^\{\{\d+\}\}$/.test(part) ? <mark key={i}>{part}</mark> : <React.Fragment key={i}>{part}</React.Fragment>));
}

// ── tabs ─────────────────────────────────────────────────────────────────────

function OffNotice({ rules, openSettings }) {
  const off = rules.filter((r) => r.enabled === false);
  if (!off.length) return null;
  return (
    <div className="nye-callout" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <strong>
          {off.length} message{off.length > 1 ? 's are' : ' is'} turned off:
        </strong>{' '}
        {off.map((r) => r.label).join(', ')}. They are not sent to anyone.
      </div>
      <button type="button" className="nye-btn nye-btn--outline nye-btn--xs" onClick={openSettings}>
        ⚙️ Change in Automation Settings
      </button>
    </div>
  );
}

function AutomationsTab({ jobs, meta, flows, reload, rules, openSettings }) {
  const [search, setSearch] = useState('');
  const [flow, setFlow] = useState('');
  const [status, setStatus] = useState('');
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null);

  const groups = useMemo(() => buildGroups(jobs), [jobs]);
  const filtered = groups.filter((g) => {
    const q = search.trim().toLowerCase();
    if (flow && g.flow !== flow) return false;
    if (status && g.status !== status) return false;
    return !q || String(g.customer_name || '').toLowerCase().includes(q) || String(g.customer_phone || '').includes(q.replace(/^\+/, ''));
  });
  const { page, setPage, pageSize, setPageSize, paginated, totalPages } = usePagination(filtered, 25);

  const stopAll = async (g) => {
    const ids = g.steps.filter((s) => s.status === 'pending').map((s) => s.id);
    if (!ids.length) return;
    if (!window.confirm(`Stop all ${ids.length} scheduled message${ids.length > 1 ? 's' : ''} for ${g.customer_name || g.customer_phone}?`)) return;
    setBusy(g.key);
    try {
      const res = await api.cancelJobs(ids);
      toast.success(`${res.cancelled} message${res.cancelled !== 1 ? 's' : ''} stopped for this customer`);
      await reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="nye-fade" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <OffNotice rules={rules} openSettings={openSettings} />
      <div className="nye-card nye-filter-bar" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', padding: 18 }}>
        <label className="nye-field" style={{ flex: 1, minWidth: 200 }}>
          <span className="nye-label--caps">Search by customer</span>
          <input className="nye-input nye-input--bordered" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or phone — leave empty to see all" />
        </label>
        <label className="nye-field" style={{ minWidth: 200 }}>
          <span className="nye-label--caps">Automation type</span>
          <select className="nye-select" value={flow} onChange={(e) => setFlow(e.target.value)}>
            <option value="">All types</option>
            {Object.entries(flows).map(([k, v]) => (
              <option key={k} value={k}>
                {FLOW_ICON[k]} {v}
              </option>
            ))}
          </select>
        </label>
        <label className="nye-field" style={{ minWidth: 170 }}>
          <span className="nye-label--caps">Status</span>
          <select className="nye-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Currently running</option>
            <option value="cancelled">Stopped</option>
            <option value="completed">Finished</option>
          </select>
        </label>
        <button
          type="button"
          className="nye-btn nye-btn--outline"
          onClick={() => {
            setSearch('');
            setFlow('');
            setStatus('');
            reload();
          }}
        >
          ↻ Reset
        </button>
      </div>

      {groups.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          {[
            ['Total found', filtered.length, 'var(--nye-purple)'],
            ['Currently running', filtered.filter((g) => g.status === 'active').length, STATUS_COLOR.active],
            ['Stopped', filtered.filter((g) => g.status === 'cancelled').length, STATUS_COLOR.cancelled],
            ['Done', filtered.filter((g) => g.status === 'completed').length, STATUS_COLOR.completed],
          ].map(([label, value, color]) => (
            <div key={label} className="nye-card" style={{ padding: 14, textAlign: 'center', boxShadow: 'none' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
              <div className="nye-small nye-muted">{label}</div>
            </div>
          ))}
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <div className="nye-card">
          <EmptyState icon="🤖" title="No automations found" body="Automated WhatsApp messages start when a booking is saved, a customer goes quiet mid-booking, or a service is marked taken." />
        </div>
      ) : (
        <div>
          {paginated.map((g) => {
            const sent = g.steps.filter((s) => DONE_STATUSES.includes(s.status)).length;
            const pending = g.steps.filter((s) => s.status === 'pending').length;
            const failed = g.steps.filter((s) => s.status === 'failed').length;
            const open = expanded[g.key];
            return (
              <div key={g.key} className="nye-card nye-flow-card" style={{ borderLeftColor: STATUS_COLOR[g.status] }}>
                <div className="nye-row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 'min(260px, 100%)' }}>
                    <div className="nye-row nye-row--wrap" style={{ gap: 8, marginBottom: 5 }}>
                      <span className="nye-strong" style={{ fontSize: 14 }}>
                        {FLOW_ICON[g.flow]} {flows[g.flow] || g.flow}
                      </span>
                      <Badge status={g.status} small />
                      <span className="nye-pill nye-pill--muted nye-pill--square">👤 {g.customer_name || 'Customer'}</span>
                      <span className="nye-pill nye-pill--square nye-mono">📱 {formatPhone(g.customer_phone)}</span>
                      {g.appointment_id ? <span className="nye-pill nye-pill--gold nye-pill--square">Appointment #{g.appointment_id}</span> : null}
                    </div>
                    <div className="nye-row nye-row--wrap nye-small" style={{ gap: 12, color: 'var(--nye-muted)' }}>
                      <span>Started {formatDateTime(g.started)}</span>
                      <span style={{ color: STATUS_COLOR.sent }}>✓ {sent} sent</span>
                      {pending ? <span style={{ color: STATUS_COLOR.pending }}>⏳ {pending} scheduled</span> : null}
                      {failed ? <span style={{ color: STATUS_COLOR.failed }}>✗ {failed} failed</span> : null}
                      <span>{g.steps.length} messages total</span>
                    </div>
                  </div>
                  <div className="nye-row" style={{ gap: 6, flexShrink: 0 }}>
                    {pending ? (
                      <button type="button" className="nye-btn nye-btn--danger nye-btn--xs" disabled={busy === g.key} onClick={() => stopAll(g)}>
                        {busy === g.key ? <Spinner /> : '🛑'} Stop All
                      </button>
                    ) : null}
                    <button type="button" className="nye-btn nye-btn--outline nye-btn--xs" onClick={() => setExpanded((e) => ({ ...e, [g.key]: !e[g.key] }))}>
                      {open ? '▲ Hide' : '▼ View Messages'}
                    </button>
                  </div>
                </div>

                {open ? (
                  <div className="nye-table-shell nye-mt-16">
                    <div className="nye-table-wrap">
                      <table className="nye-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Message</th>
                            <th>Scheduled For</th>
                            <th>Status</th>
                            <th>Template Used</th>
                            <th>Sent As</th>
                            <th>Sent At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.steps.map((s, i) => {
                            const m = meta[s.kind] || { label: s.kind, when: '' };
                            return (
                              <tr key={s.id}>
                                <td className="nye-td-muted">{i + 1}</td>
                                <td style={{ maxWidth: 420 }}>
                                  <div className="nye-strong" style={{ fontSize: 12 }}>
                                    {m.label}
                                  </div>
                                  {s.condition ? <div className="nye-small nye-gold-text">Only if they have not booked again</div> : null}
                                  <div className="nye-small nye-muted" style={{ marginTop: 3, lineHeight: 1.4 }}>
                                    {s.body}
                                  </div>
                                  {s.error ? <div className="nye-small" style={{ color: 'var(--nye-danger)', marginTop: 3 }}>{s.error}</div> : null}
                                </td>
                                <td className="nye-td-mono">{formatDateTime(s.send_at)}</td>
                                <td>
                                  <Badge status={s.status} small />
                                </td>
                                <td className="nye-td-mono" style={{ color: 'var(--nye-purple)' }}>
                                  {s.template_name}
                                </td>
                                <td>
                                  <SentAs value={s.sent_as} />
                                </td>
                                <td className="nye-td-mono">{s.sent_at ? formatDateTime(s.sent_at) : '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
          <Pagination page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} />
        </div>
      )}
    </div>
  );
}

function HistoryTab({ jobs, meta, reload }) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^\+/, '');
    return [...(Array.isArray(jobs) ? jobs : [])]
      .filter((j) => (!status || j.status === status) && (!q || String(j.customer_name || '').toLowerCase().includes(q) || String(j.customer_phone || '').includes(q)))
      .sort((a, b) => byDate(b.send_at, a.send_at));
  }, [jobs, search, status]);
  const { page, setPage, pageSize, setPageSize, paginated, totalPages } = usePagination(rows, 25);

  const cancel = async (job) => {
    setBusy(job.id);
    try {
      await api.cancelJob(job.id);
      toast.success('Message cancelled');
      await reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="nye-fade" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="nye-card nye-filter-bar" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', padding: 18 }}>
        <label className="nye-field" style={{ flex: 1, minWidth: 200 }}>
          <span className="nye-label--caps">Search by customer</span>
          <input className="nye-input nye-input--bordered" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or phone — leave empty to see all" />
        </label>
        <label className="nye-field" style={{ minWidth: 210 }}>
          <span className="nye-label--caps">Filter by status</span>
          <select className="nye-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All messages</option>
            <option value="pending">⏳ Waiting to send</option>
            <option value="sent">✅ Sent</option>
            <option value="delivered">📬 Delivered</option>
            <option value="read">👁️ Read by customer</option>
            <option value="failed">❌ Failed</option>
            <option value="cancelled">🚫 Cancelled</option>
          </select>
        </label>
        <button type="button" className="nye-btn nye-btn--outline" onClick={reload}>
          ↻ Refresh
        </button>
      </div>

      <div className="nye-table-shell">
        <div className="nye-small nye-muted" style={{ padding: '12px 16px', borderBottom: '1px solid var(--nye-border)', fontWeight: 600 }}>
          {rows.length} message{rows.length !== 1 ? 's' : ''}
        </div>
        {rows.length === 0 ? (
          <EmptyState icon="📭" title="No messages yet" body="Messages appear here once a booking, an idle chat, or a finished service queues them." />
        ) : (
          <div className="nye-table-wrap">
            <table className="nye-table">
              <thead>
                <tr>
                  <th>Message Type</th>
                  <th>Sent To</th>
                  <th>Template</th>
                  <th>Scheduled For</th>
                  <th>Delivery Status</th>
                  <th>Sent As</th>
                  <th>Error</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((j) => (
                  <tr key={j.id}>
                    <td className="nye-td-strong" style={{ fontSize: 12 }}>
                      {(meta[j.kind] || {}).label || j.kind}
                    </td>
                    <td>
                      <div>{j.customer_name || '—'}</div>
                      <div className="nye-td-mono">{formatPhone(j.customer_phone)}</div>
                    </td>
                    <td className="nye-td-mono" style={{ color: 'var(--nye-purple)' }}>
                      {j.template_name}
                    </td>
                    <td className="nye-td-mono">{formatDateTime(j.send_at)}</td>
                    <td>
                      <Badge status={j.status} small />
                      {j.sent_at ? <div className="nye-small nye-muted" style={{ marginTop: 2 }}>{formatDateTime(j.sent_at)}</div> : null}
                    </td>
                    <td>
                      <SentAs value={j.sent_as} />
                    </td>
                    <td className="nye-small" style={{ maxWidth: 180, color: 'var(--nye-danger)', wordBreak: 'break-word' }}>
                      {j.error || '—'}
                    </td>
                    <td>
                      <div className="nye-row" style={{ gap: 5 }}>
                        <button type="button" className="nye-btn nye-btn--outline nye-btn--xs" onClick={() => setView(j)}>
                          👁 View
                        </button>
                        {j.status === 'pending' ? (
                          <button type="button" className="nye-btn nye-btn--danger nye-btn--xs" disabled={busy === j.id} onClick={() => cancel(j)}>
                            ✕ Cancel
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination attached page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} />
      </div>

      {view ? (
        <Modal title={(meta[view.kind] || {}).label || 'Message'} onClose={() => setView(null)}>
          <div className="nye-small nye-muted" style={{ marginBottom: 10 }}>
            To {view.customer_name || 'customer'} · {formatPhone(view.customer_phone)} · {formatDateTime(view.send_at)}
          </div>
          <div className="nye-section-label">{view.sent_as === 'text' ? 'Sent as plain text (24-hour window)' : 'WhatsApp template'}</div>
          <div className="nye-template-body">{view.sent_as === 'text' ? view.session_body : view.body}</div>
          {view.sent_as !== 'text' && view.session_body && view.session_body !== view.body ? (
            <>
              <div className="nye-section-label nye-mt-16">Text used if the customer wrote in the last 24 hours</div>
              <div className="nye-template-body">{view.session_body}</div>
            </>
          ) : null}
          <div className="nye-row nye-mt-16" style={{ gap: 8 }}>
            <Badge status={view.status} />
            <span className="nye-mono nye-muted">{view.template_name}</span>
            <SentAs value={view.sent_as} />
          </div>
          {view.error ? <div className="nye-callout nye-callout--danger nye-mt-16">{view.error}</div> : null}
        </Modal>
      ) : null}
    </div>
  );
}

function TemplatesTab({ data, rules, flows, refresh, checking }) {
  const [flow, setFlow] = useState('');
  const shown = rules.filter((r) => !flow || r.flow === flow);
  const summary = data?.meta_summary;
  if (!data) {
    return (
      <div className="nye-callout nye-callout--danger nye-fade">
        <strong>The template list could not be loaded.</strong> Restart the demo backend so it serves the approved WhatsApp templates, then
        open this tab again.
      </div>
    );
  }
  const failedCheck = Boolean(data.meta_error);
  const allGood = summary && !failedCheck && summary.problems.length === 0;
  const tone = failedCheck ? '' : allGood ? ' nye-callout--info' : ' nye-callout--danger';
  return (
    <div className="nye-fade" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className={`nye-callout${tone}`} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          {failedCheck ? (
            <>
              <strong>Could not check the WhatsApp account.</strong> {data.meta_error}
            </>
          ) : allGood ? (
            <>
              <strong>
                All {summary.templates} templates are approved on the {SALON_NAME} WhatsApp account
              </strong>{' '}
              and match the text below. Nothing needs to be submitted to Meta.
            </>
          ) : (
            <>
              <strong>
                {summary?.problems.length} of {summary?.templates} templates need attention on WhatsApp:
              </strong>{' '}
              {summary?.problems.join(', ')}. Messages using them fail until they are approved.
            </>
          )}
        </div>
        <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={refresh} disabled={checking}>
          {checking ? <Spinner /> : '↻'} Check again
        </button>
      </div>

      <div className="nye-card nye-filter-bar" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', padding: 18 }}>
        <label className="nye-field" style={{ minWidth: 240 }}>
          <span className="nye-label--caps">Message group</span>
          <select className="nye-select" value={flow} onChange={(e) => setFlow(e.target.value)}>
            <option value="">All templates</option>
            {Object.entries(flows).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <div className="nye-hint" style={{ flex: 1, minWidth: 240 }}>
          Approved WhatsApp templates, sent in English (en). Highlighted numbers are filled in for each customer, and the business name is always{' '}
          {SALON_NAME}. If the customer wrote in the last 24 hours, the plain-text version is sent instead.
        </div>
      </div>

      <div className="nye-g2 nye-g2--auto">
        {shown.map((r) => {
          const check = META_CHECK[r.meta_check] || META_CHECK.unknown;
          return (
            <div key={r.kind} className="nye-card">
              <div className="nye-row nye-row--between nye-row--wrap" style={{ marginBottom: 6, gap: 8 }}>
                <div className="nye-strong">
                  {FLOW_ICON[r.flow]} {r.label}
                </div>
                <div className="nye-row" style={{ gap: 6 }}>
                  {r.enabled === false ? <span className="nye-pill nye-pill--sm nye-pill--muted">Turned off</span> : null}
                  <span className={`nye-pill nye-pill--sm nye-pill--${check.tone}`}>{check.label}</span>
                </div>
              </div>
              <div className="nye-row nye-row--wrap" style={{ gap: 6, marginBottom: 8 }}>
                <span className="nye-pill nye-pill--square nye-mono">{r.template_name}</span>
                <span className="nye-pill nye-pill--muted nye-pill--square nye-pill--sm">{r.category}</span>
                <span className="nye-pill nye-pill--muted nye-pill--square nye-pill--sm">{r.language}</span>
              </div>
              <div className="nye-small nye-muted" style={{ marginBottom: 10 }}>
                {r.when}
              </div>
              <div className="nye-template-body">{highlight(r.body)}</div>
              <div className="nye-row nye-row--wrap" style={{ gap: 10, marginTop: 8 }}>
                {r.variables.map((v) => (
                  <span key={v.position} className="nye-small nye-muted">
                    <mark className="nye-var-chip">{`{{${v.position}}}`}</mark> {v.label}
                  </span>
                ))}
              </div>
              {r.meta_check === 'body_differs' && r.meta_body ? (
                <div className="nye-callout nye-callout--danger nye-mt-8">
                  <strong>Approved text on WhatsApp:</strong> {r.meta_body}
                </div>
              ) : null}
              <div className="nye-section-label nye-mt-16">Customer sees (template)</div>
              <div className="nye-small" style={{ color: 'var(--nye-text)', lineHeight: 1.55 }}>
                {r.preview}
              </div>
              {r.has_session_text ? (
                <>
                  <div className="nye-section-label nye-mt-16">Inside the 24-hour window (plain text)</div>
                  <div className="nye-small" style={{ color: 'var(--nye-text)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                    {r.session_preview}
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function Automation() {
  const [tab, setTab] = useState('automations');
  const [jobs, setJobs] = useState([]);
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const loadJobs = () =>
    api
      .listJobs({ limit: 2000 })
      .then(setJobs)
      .catch((e) => toast.error(e.message || 'Failed to load messages'));

  const loadTemplates = (refresh = false) =>
    api
      .listTemplates(refresh)
      .then(setTemplates)
      .catch((e) => toast.error(e.message));

  const recheck = async () => {
    setChecking(true);
    await loadTemplates(true);
    setChecking(false);
  };

  useEffect(() => {
    Promise.all([loadJobs(), loadTemplates()]).finally(() => setLoading(false));
  }, []);

  const flows = templates?.flows || FLOW_NAMES;
  const rules = Array.isArray(templates?.rules) ? templates.rules : [];
  const jobRows = Array.isArray(jobs) ? jobs : [];
  const meta = useMemo(() => Object.fromEntries(rules.map((r) => [r.kind, r])), [rules]);
  const pending = jobRows.filter((j) => j.status === 'pending').length;

  return (
    <div className="nye-fade">
      <div className="nye-auto-head">
        <div className="nye-auto-head-icon">🔔</div>
        <div style={{ flex: 1 }}>
          <div className="nye-strong" style={{ fontSize: 15 }}>
            Automated Messages
          </div>
          <div className="nye-small nye-muted">
            {SALON_NAME} · WhatsApp confirmations, reminders and follow-ups · {pending} waiting to send
          </div>
        </div>
        <span className="nye-pill nye-pill--gold">💇 Salon</span>
      </div>
      <div className="nye-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" className={`nye-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="nye-tab-body">
        {loading ? (
          <Loader />
        ) : (
          <>
            {tab === 'automations' ? (
              <AutomationsTab jobs={jobRows} meta={meta} flows={flows} reload={loadJobs} rules={rules} openSettings={() => setTab('settings')} />
            ) : null}
            {tab === 'history' ? <HistoryTab jobs={jobRows} meta={meta} reload={loadJobs} /> : null}
            <div style={{ display: tab === 'settings' ? 'block' : 'none' }}>
              <AutomationSettings onSaved={() => Promise.all([loadJobs(), loadTemplates()])} />
            </div>
            {tab === 'templates' ? <TemplatesTab data={templates} rules={rules} flows={flows} refresh={recheck} checking={checking} /> : null}
          </>
        )}
      </div>
    </div>
  );
}
